import { useEffect, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { Trash2, Pencil, Download, Upload } from "lucide-react";
import { toast } from "sonner";

const NATURES = [
    { value: "asset", label: "Asset" },
    { value: "liability", label: "Liability" },
    { value: "income", label: "Income" },
    { value: "expense", label: "Expense" },
    { value: "equity", label: "Equity" },
];

function num(n) {
    return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function GeneralLedgersPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("general_ledgers", "write");
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState({ nature: "all", segment: "all" });
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(blank());
    const fileRef = useRef(null);

    function blank() {
        return {
            name: "", code: "", nature: "asset", segment: "Voice",
            currency: "USD", description: "", active: true,
            opening_debit: "", opening_credit: "", fx_rate: "",
        };
    }

    const load = async () => {
        const params = {};
        if (filter.nature !== "all") params.nature = filter.nature;
        if (filter.segment !== "all") params.segment = filter.segment;
        const { data } = await api.get("/general-ledgers", { params });
        setRows(data || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const startEdit = (r) => {
        setEditId(r.id);
        setForm({
            name: r.name, code: r.code || "", nature: r.nature, segment: r.segment,
            currency: r.currency, description: r.description || "",
            active: r.active !== false,
            opening_debit: r.opening_debit || "", opening_credit: r.opening_credit || "",
            fx_rate: "",
        });
        setOpen(true);
    };
    const close = () => { setOpen(false); setEditId(null); setForm(blank()); };

    const submit = async () => {
        if (!form.name.trim()) return toast.error("Name is required");
        const body = {
            name: form.name, code: form.code, nature: form.nature, segment: form.segment,
            currency: form.currency.toUpperCase(),
            description: form.description, active: form.active,
            opening_debit: Number(form.opening_debit || 0),
            opening_credit: Number(form.opening_credit || 0),
            fx_rate: form.fx_rate ? Number(form.fx_rate) : null,
        };
        try {
            if (editId) await api.put(`/general-ledgers/${editId}`, body);
            else await api.post("/general-ledgers", body);
            toast.success("Saved");
            close();
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this ledger?")) return;
        try {
            await api.delete(`/general-ledgers/${id}`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    const downloadTemplate = async () => {
        const token = localStorage.getItem("erp_access_token");
        const res = await fetch(`${API}/general-ledgers/template`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "general_ledgers_template.csv";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    const onUploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/general-ledgers/upload", fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            const skipped = (data.skipped || []).length;
            toast.success(`Imported — ${data.inserted} new, ${data.updated} updated${skipped ? `, ${skipped} skipped` : ""}`);
            if (skipped) toast.warning(`Skipped: ${data.skipped.slice(0, 3).join("; ")}${data.skipped.length > 3 ? "…" : ""}`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally { e.target.value = ""; }
    };

    return (
        <div data-testid="general-ledgers-page">
            <PageHeader
                title="General Ledgers"
                eyebrow="Chart of Accounts"
                description="Asset, liability, income, expense, equity accounts. Each ledger has its own opening balance."
                action={canWrite ? (
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={downloadTemplate} className="rounded-sm h-9" data-testid="gl-template-btn"><Download size={13} className="mr-1.5"/>Template (.csv)</Button>
                        <input ref={fileRef} type="file" accept=".csv" onChange={onUploadFile} hidden/>
                        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-sm h-9" data-testid="gl-import-btn"><Upload size={13} className="mr-1.5"/>Bulk import</Button>
                        <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : close()}>
                            <DialogTrigger asChild>
                                <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="gl-add-btn">+ New ledger</Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm max-w-2xl" data-testid="gl-dialog">
                                <DialogHeader><DialogTitle>{editId ? "Edit ledger" : "Add ledger"}</DialogTitle></DialogHeader>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Name *">
                                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9" data-testid="gl-name-input"/>
                                    </Field>
                                    <Field label="Code (optional)">
                                        <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="gl-code-input"/>
                                    </Field>
                                    <Field label="Nature *">
                                        <Select value={form.nature} onValueChange={(v) => setForm({ ...form, nature: v })}>
                                            <SelectTrigger className="rounded-sm" data-testid="gl-nature-select"><SelectValue/></SelectTrigger>
                                            <SelectContent>{NATURES.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Segment *">
                                        <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v, currency: v === "Voice" ? "USD" : "EUR" })}>
                                            <SelectTrigger className="rounded-sm" data-testid="gl-segment-select"><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Voice">Voice (USD base)</SelectItem>
                                                <SelectItem value="SMS">SMS (EUR base)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Currency *">
                                        <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="rounded-sm h-9 font-mono" data-testid="gl-currency-input"/>
                                    </Field>
                                    <Field label={editId ? "FX rate (read-only on edit)" : "FX rate (optional)"}>
                                        <Input type="number" step="0.000001" value={form.fx_rate} disabled={!!editId} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })} className="rounded-sm h-9 font-mono" placeholder="auto if blank" data-testid="gl-fx-input"/>
                                    </Field>
                                    <Field label="Opening Debit">
                                        <Input type="number" step="0.01" value={form.opening_debit} onChange={(e) => setForm({ ...form, opening_debit: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="gl-opening-debit-input"/>
                                    </Field>
                                    <Field label="Opening Credit">
                                        <Input type="number" step="0.01" value={form.opening_credit} onChange={(e) => setForm({ ...form, opening_credit: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="gl-opening-credit-input"/>
                                    </Field>
                                    <div className="col-span-2">
                                        <Field label="Description">
                                            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" data-testid="gl-description-input"/>
                                        </Field>
                                    </div>
                                    <Field label="Active">
                                        <label className="flex items-center gap-2 text-[12.5px] text-slate-700 h-9 px-3 border border-slate-200 rounded-sm bg-white">
                                            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="gl-active-input" className="accent-[#002FA7]"/>
                                            <span>Show in pickers</span>
                                        </label>
                                    </Field>
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" onClick={close}>Cancel</Button>
                                    <Button onClick={submit} className="rounded-sm bg-[#002FA7]" data-testid="gl-save-btn">Save</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                ) : null}
            />

            <div className="flex flex-wrap gap-3 mb-3 items-center">
                <Select value={filter.nature} onValueChange={(v) => setFilter({ ...filter, nature: v })}>
                    <SelectTrigger className="w-44 rounded-sm h-9" data-testid="gl-filter-nature"><SelectValue/></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All natures</SelectItem>
                        {NATURES.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filter.segment} onValueChange={(v) => setFilter({ ...filter, segment: v })}>
                    <SelectTrigger className="w-44 rounded-sm h-9" data-testid="gl-filter-segment"><SelectValue/></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All segments</SelectItem>
                        <SelectItem value="Voice">Voice</SelectItem>
                        <SelectItem value="SMS">SMS</SelectItem>
                    </SelectContent>
                </Select>
                <span className="text-[11px] text-slate-500">{rows.length} ledger(s)</span>
            </div>

            <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-[13px] financial min-w-[1100px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-3 py-2.5">Name</th>
                            <th className="px-3 py-2.5">Code</th>
                            <th className="px-3 py-2.5">Nature</th>
                            <th className="px-3 py-2.5">Segment</th>
                            <th className="px-3 py-2.5">Currency</th>
                            <th className="px-3 py-2.5 num">Opening Dr</th>
                            <th className="px-3 py-2.5 num">Opening Cr</th>
                            <th className="px-3 py-2.5 num">Opening (Base)</th>
                            <th className="px-3 py-2.5 w-24"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-500 py-10">No general ledgers yet</td></tr>}
                        {rows.map((r) => {
                            const opBase = (r.opening_debit_base || 0) - (r.opening_credit_base || 0);
                            return (
                                <tr key={r.id} className="border-b border-slate-100" data-testid={`gl-row-${r.id}`}>
                                    <td className="px-3 py-2 font-medium">{r.name}</td>
                                    <td className="px-3 py-2 font-mono text-[11.5px] text-slate-500">{r.code || "—"}</td>
                                    <td className="px-3 py-2"><span className="text-[10px] uppercase tracking-widest border border-slate-200 bg-slate-50 px-1.5 py-0.5">{r.nature}</span></td>
                                    <td className="px-3 py-2">{r.segment}</td>
                                    <td className="px-3 py-2 font-mono">{r.currency}</td>
                                    <td className="px-3 py-2 num">{r.opening_debit ? num(r.opening_debit) : "—"}</td>
                                    <td className="px-3 py-2 num">{r.opening_credit ? num(r.opening_credit) : "—"}</td>
                                    <td className="px-3 py-2 num font-semibold">{opBase >= 0 ? num(opBase) : `(${num(Math.abs(opBase))})`} <span className="text-[10px] text-slate-400">{r.base_currency}</span></td>
                                    <td className="px-3 py-2">
                                        {canWrite && (
                                            <div className="flex gap-1">
                                                <Button variant="ghost" size="sm" onClick={() => startEdit(r)} className="h-7 px-2" data-testid={`gl-edit-${r.id}`}><Pencil size={13}/></Button>
                                                <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="h-7 px-2 text-red-600" data-testid={`gl-delete-${r.id}`}><Trash2 size={13}/></Button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
